import { useEffect, useRef, useState } from 'react';
import { loadKakaoMaps } from '../../lib/kakaoMaps';
import { tCalendar } from '../../lib/calendar-i18n';
import { tTravel } from './lib/travelI18n';
import { attachMapGestures, fitWhenShown } from './lib/mapGestures';
import { MapZoomButtons } from './MapZoomButtons';
import './LocationPicker.css';
import './TravelMap.css';
import './MapControls.css';

/**
 * 출발지 → 일정 장소 «실제 길» 지도 (2026-10-13 · 옛 가지 d2509ef7a·d655e0e18 를 다시 쓴 것).
 *
 * ★ 옛 판은 두 점 사이에 «직선»을 그었다. 사용자는 실제 길을 원한다 — 서버가 카카오모빌리티의
 *   `vertexes` 를 [lat,lng] 쌍으로 펴서 주고(`path`), 여기서 그 점들을 Polyline 으로 잇는다.
 *   길이 없으면(path 가 비면) 핀 둘만 그리고 선은 긋지 않는다 — 직선을 «길인 척» 그리지 않는다.
 *
 * ⚠ 핀 모양은 `LocationPicker.css` 의 `.travel-map-pin(--dest)` 를 그대로 쓴다 — 그 파일 머리말이
 *   「TravelMap 이 오면 옮겨라」라고 적어 두었지만, 옮기면 기존 파일을 고치게 되어 이 판에서는
 *   불러 쓰기만 한다(두 벌을 만들지 않는다).
 * ⚠ 선 색은 카카오에 «글자»로 넘겨야 해서 토큰(`--primary`)을 그 자리에서 읽어 넘긴다 — 인라인 hex 없음.
 */
export interface TravelMapProps {
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
  path: ReadonlyArray<readonly [number, number]>;
}

function readToken(el: Element, name: string): string {
  try { return getComputedStyle(el).getPropertyValue(name).trim(); } catch { return ''; }
}

export function TravelMap({ origin, destination, path }: TravelMapProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [map, setMap] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    let detachGestures: (() => void) | undefined;
    let detachFit: (() => void) | undefined;
    setStatus('loading');
    setMap(null);
    loadKakaoMaps()
      .then(() => {
        const el = containerRef.current;
        const root = rootRef.current;
        if (cancelled || !el || !root) return;
        const { kakao } = window;
        const o = new kakao.maps.LatLng(origin.lat, origin.lng);
        const d = new kakao.maps.LatLng(destination.lat, destination.lng);
        const map = new kakao.maps.Map(el, { center: d, level: 6 });
        detachGestures = attachMapGestures(map, root);

        const bounds = new kakao.maps.LatLngBounds();
        bounds.extend(o);
        bounds.extend(d);
        if (path.length >= 2) {
          const line = path.map(([lat, lng]) => new kakao.maps.LatLng(lat, lng));
          for (const p of line) bounds.extend(p);
          new kakao.maps.Polyline({
            map,
            path: line,
            strokeWeight: 5,
            strokeColor: readToken(el, '--primary') || readToken(el, '--text'),
            strokeOpacity: 0.85,
            strokeStyle: 'solid',
          });
        }
        new kakao.maps.CustomOverlay({ map, position: o, yAnchor: 0.5, content: '<div class="travel-map-pin"></div>' });
        new kakao.maps.CustomOverlay({ map, position: d, yAnchor: 1, content: '<div class="travel-map-pin travel-map-pin--dest"></div>' });
        const fit = () => map.setBounds(bounds);
        fit();
        detachFit = fitWhenShown(map, el, fit);
        setMap(map);
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; detachGestures?.(); detachFit?.(); };
  }, [origin.lat, origin.lng, destination.lat, destination.lng, path]);

  return (
    <div ref={rootRef} className="travel-map" role="group" aria-label={tTravel('map_aria')} data-status={status}>
      <div ref={containerRef} className="travel-map-canvas" />
      {map && <MapZoomButtons map={map} />}
      {status === 'error' && (
        <div className="travel-map-fallback">{tCalendar('calendar.event_create.location_map_load_failed')}</div>
      )}
    </div>
  );
}
